import { cn } from "@/lib/utils";

const GRADIENTS = [
  "from-violet-500 to-purple-400",
  "from-violet-500 to-purple-400",
  "from-orange-500 to-amber-400",
  "from-emerald-500 to-teal-400",
  "from-pink-500 to-rose-400",
  "from-purple-500 to-violet-400",
  "from-red-500 to-orange-400",
  "from-green-500 to-lime-400",
];

function hashName(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash << 5) - hash + name.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

type BuildStatus =
  | "queued"
  | "preparing"
  | "building"
  | "publishing"
  | "completed"
  | "failed"
  | "cancelled";

const statusBorderColors: Record<BuildStatus, string> = {
  completed: "border-green-500",
  failed: "border-red-500",
  cancelled: "border-zinc-500",
  queued: "border-yellow-500",
  preparing: "border-violet-500",
  building: "border-violet-500",
  publishing: "border-purple-500",
};

interface AppAvatarProps {
  name: string;
  size?: "sm" | "md" | "lg";
  status?: BuildStatus;
  className?: string;
}

export function AppAvatar({ name, size = "md", status, className = "" }: AppAvatarProps) {
  const gradient = GRADIENTS[hashName(name) % GRADIENTS.length];
  const initials = name
    .split(/[\s-_]+/)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const innerSize = {
    sm: "size-8 text-xs",
    md: "size-10 text-sm",
    lg: "size-14 text-lg",
  }[size];

  // Outer ring wrapper sizes (inner + 3px border + 2px gap on each side = +10px)
  const outerSize = {
    sm: "size-[42px]",
    md: "size-[52px]",
    lg: "size-[66px]",
  }[size];

  if (!status) {
    return (
      <div
        className={cn(
          "flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br font-semibold text-white",
          gradient,
          innerSize,
          className
        )}
      >
        {initials}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full border-[3px]",
        statusBorderColors[status],
        outerSize,
        className
      )}
    >
      <div
        className={cn(
          "flex items-center justify-center rounded-full bg-gradient-to-br font-semibold text-white",
          gradient,
          innerSize,
        )}
      >
        {initials}
      </div>
    </div>
  );
}
